
import { Component, Mixins, Prop } from 'vue-property-decorator'
import Testable from '@/mixins/Testable'
import { Transport } from '@/models/Transport'
import { TerminalVisit } from '@/models/TerminalVisit'
import { BillOfLading } from '@/models/BillOfLading'
import { namespace } from 'vuex-class'
import VesselDocuments from '@/views/Preferences/VesselDocuments.vue'
import User from '@/models/User'
import LoadingBillNewDialog from '../views/LoadingBill/NewDialog.vue'
import InvoiceCreateDialog from '../views/Invoicing/CreateDialog.vue'
import InvoiceEditDialog from '../views/Invoicing/EditDialog.vue'
import InvoiceGeneratorDialogWrapper from '../views/Invoicing/GeneratorDialog.vue'
import InvoiceEmailDialog from '../views/Invoicing/EmailDialog.vue'
import ExternalDownloadDialog from '../views/Invoicing/ExternalDownloadDialog.vue'
import InvoiceType from '@/enums/InvoiceType'
import { Invoice } from '@/models/accounting/Invoice'
import { generateInvoicePdf, GenerateInvoiceData } from '@/utils/invoice'
import Notification from '@/classes/Notification'
import i18n from '@/i18n'
import { isV3DeepLink, v3PagePath } from '@/utils/v3Page'

const Viewport = namespace('viewport')

@Component({
	components: {
		VesselDocuments,
		LoadingBillNewDialog,
		InvoiceCreateDialog,
		InvoiceEditDialog,
		InvoiceGeneratorDialogWrapper,
		InvoiceEmailDialog,
		ExternalDownloadDialog,
	}
})
export default class V3Page extends Mixins(Testable) {
	@Viewport.Getter('isMobile')
	isMobile

	@Prop({ required: true })
	page!: string

	@Prop({ default: null })
	routeParams

	@Prop({ default: false })
	isDialog!: boolean

	msgEventListener!: (evt: MessageEvent) => void

	lastVisitedUrl: string = ''
	transportAndVisitLoading = false
	openBillOfLadingGenerator: null | {
		transport: Transport | null
		terminalVisit: TerminalVisit
		onSubmit: (bill: BillOfLading) => void
	} = null
	vesselId: null | string = null
	iframeSrc: string = ''
	isVesselDocumentsModalOpen: boolean | null = null
	isLoadingBillFormModalOpen: boolean | null = null

	billOfLadingType: 'loading-bill' | 'unloading-bill' = 'unloading-bill'

	// Invoice dialog state
	isInvoiceCreateDialogOpen: boolean | null = null
	invoiceCreateType: InvoiceType = InvoiceType.SALES
	invoiceCreateCallback: ((response?: any) => void) | null = null

	isInvoiceEditDialogOpen: boolean | null = null
	invoiceEditId: string | null = null
	invoiceEditCallback: ((response?: any) => void) | null = null

	isInvoiceGeneratorDialogOpen: boolean | null = null
	invoiceGeneratorId: string | null = null
	invoiceGeneratorCallback: ((response?: any) => void) | null = null

	isInvoiceEmailDialogOpen: boolean | null = null
	invoiceEmailId: string | null = null
	invoiceEmailCallback: ((response?: any) => void) | null = null

	isExternalDownloadDialogOpen: boolean | null = null
	externalDownloadInvoiceId: string | null = null
	externalDownloadCallback: ((response?: any) => void) | null = null

	created() {
		this.iframeSrc = this.baseUrl + this.withRouteParams(this.page)
	}

	withRouteParams(page: string): string {
		const url = new URL('http://example.com' + page)

		if (this.isDialog) {
			url.searchParams.append('dialog', 'true')
		}

		if (!this.routeParams) return url.toString().replace('http://example.com', '')

		Object.entries(this.routeParams).forEach(([key, value]) => {
			if (value !== null && value !== undefined) {
				url.searchParams.append(key, String(value))
			}
		})

		return url.toString().replace('http://example.com', '')
	}

	get localStorageKey() {
		return v3PagePath(this.page) + '_last_url'
	}

	get baseUrl(): string {
		return String(process.env.VUE_APP_TERMINAL_MODULE_URL).replace(/.*(\/)$/gm, '')
	}

	get spaURL(): string {
		if (this.lastVisitedUrl && this.lastVisitedUrl.startsWith(v3PagePath(this.page))) return this.lastVisitedUrl
		return this.page
	}

	get iframeElement(): HTMLIFrameElement | null {
		return (this.$refs.iframe as HTMLIFrameElement) ?? null
	}

	handleRouteChanged(url: string) {
		if (url.startsWith(v3PagePath(this.page))) window.localStorage.setItem(this.localStorageKey, url)
	}

	beforeCreate(): void {
		this.msgEventListener = (event: MessageEvent): void => {
			const data = String(event.data)

			if (!data.startsWith('xframe:')) {
				return
			}

			if (data.startsWith('xframe:fire:')) {
				const eventAndArgs = data.replace('xframe:fire:', '').split(':')
				const event = eventAndArgs[0]
				const args = eventAndArgs.slice(1).join(':')

				return this.handleEvent(event, args ? JSON.parse(args) : null)
			}

			if (data.startsWith('xframe:call:request:')) {
				const eventUuidAndArgs = data.replace('xframe:call:request:', '').split(':')
				const event = eventUuidAndArgs[0]
				const uuid = eventUuidAndArgs[1]

				const args = eventUuidAndArgs.slice(2).join(':')

				this.handleCall(event, args ? JSON.parse(args) : null, (response) => {
					const terminalIframeOrigin = this.spaURL.startsWith('http')
						? new URL(this.spaURL).origin
						: window.location.origin

					this.iframeElement?.contentWindow?.postMessage(
						`xframe:call:response:${event}:${uuid}:${JSON.stringify(response || null)}`,
						terminalIframeOrigin
					)
				})

				return
			}

			throw new Error(`Could not handle ${event.data}`)
		}

		window.addEventListener('message', this.msgEventListener)
	}

	handleEvent(event: string, args: any = {}): void {
		switch (event) {
			case 'navigate':
				this.$router.push(args)
				break
			case 'routeChanged':
				this.handleRouteChanged(args)
				break
			case 'syncUser':
				User.sync(true)
				break
			case 'profileUpdated':
				this.handleProfileUpdated(args)
				break
			case 'closeDialog':
				this.$emit('closeDialog', args)
				break
			default:
				throw new Error(`Unknown event ${event}`)
		}
	}

	async handleCall(event: string, args: any, cb: (response?: any) => void): Promise<any> {
		switch (event) {
			case 'show-loading-bill-modal':
				await this.handleLoadingBillModal(args, cb)
				break
			case 'show-vessel-documents-modal':
				await this.handleVesselDocumentsModal(args, cb)
				break
			case 'show-loading-bill-form-modal':
				await this.handleLoadingBillFormModal(cb)
				break
			case 'show-invoice-creator-modal':
				await this.handleInvoiceCreatorModal(args, cb)
				break
			case 'show-invoice-editor-modal':
				await this.handleInvoiceEditorModal(args, cb)
				break
			case 'show-invoice-generator-modal':
				await this.handleInvoiceGeneratorModal(args, cb)
				break
			case 'show-invoice-email-modal':
				await this.handleInvoiceEmailModal(args, cb)
				break
			case 'show-external-invoice-download-modal':
				await this.handleExternalInvoiceDownloadModal(args, cb)
				break
			default:
				throw new Error(`Unknown event ${event}`)
		}
	}

	handleInvoiceCreatorModal(
		args: { invoiceType?: InvoiceType },
		callback: (response?: any) => void
	): void {
		this.invoiceCreateType = args?.invoiceType ?? InvoiceType.SALES
		this.isInvoiceCreateDialogOpen = true
		this.invoiceCreateCallback = callback
	}

	handleInvoiceEditorModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.invoiceEditId = args.invoiceId
		this.isInvoiceEditDialogOpen = true
		this.invoiceEditCallback = callback
	}

	handleInvoiceGeneratorModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.invoiceGeneratorId = args.invoiceId
		this.isInvoiceGeneratorDialogOpen = true
		this.invoiceGeneratorCallback = callback
	}

	handleInvoiceEmailModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.invoiceEmailId = args.invoiceId
		this.isInvoiceEmailDialogOpen = true
		this.invoiceEmailCallback = callback
	}

	handleExternalInvoiceDownloadModal(
		args: { invoiceId: string },
		callback: (response?: any) => void
	): void {
		this.externalDownloadInvoiceId = args.invoiceId
		this.isExternalDownloadDialogOpen = true
		this.externalDownloadCallback = callback
	}

	// Invoice dialog close/success handlers

	onInvoiceCreateClose(): void {
		this.isInvoiceCreateDialogOpen = null
		this.invoiceCreateCallback?.(null)
		this.invoiceCreateCallback = null
	}

	onInvoiceCreateSaved(event?: any): void {
		this.fireToSpa('invoiceCreated', event ?? {})
		this.invoiceCreateCallback?.(event)
		this.invoiceCreateCallback = null
		this.isInvoiceCreateDialogOpen = null
	}

	onInvoiceEditClose(): void {
		this.isInvoiceEditDialogOpen = null
		this.invoiceEditCallback?.(null)
		this.invoiceEditCallback = null
	}

	onInvoiceEditSaved(event?: any): void {
		this.fireToSpa('invoiceUpdated', event)
		this.invoiceEditCallback?.(event)
		this.invoiceEditCallback = null
		this.isInvoiceEditDialogOpen = null
	}

	onInvoiceGeneratorClose(): void {
		this.isInvoiceGeneratorDialogOpen = null
		this.invoiceGeneratorCallback?.(null)
		this.invoiceGeneratorCallback = null
	}

	// Closes the dialog first and only then talks to the backend, so the user isn't kept waiting
	// on generate + the attachments patch. This lives here rather than in the dialog because the
	// dialog is v-if'd away on close: it would unmount mid-request and its $emit would go nowhere.
	async onInvoiceGenerateRequest(payload: {
		invoice: Invoice
		receivedData: GenerateInvoiceData | null
	}): Promise<void> {
		const invoiceId = this.invoiceGeneratorId
		const resolveSpaCall = this.invoiceGeneratorCallback
		let result: { documentId: string } | null = null

		this.isInvoiceGeneratorDialogOpen = null
		this.invoiceGeneratorCallback = null

		// The SPA cannot tell "generating in the background" from "dialog cancelled" on its own:
		// both leave its call pending. This is what switches its row indicator on; the
		// invoiceUpdated below switches it off again.
		this.fireToSpa('invoiceGenerating', { invoiceId })

		try {
			const { documentId } = await generateInvoicePdf(payload.invoice, payload.receivedData)
			result = { documentId }
		} catch (err) {
			// eslint-disable-next-line
			console.error('Error generating invoice PDF', err)
			await Notification.Error(i18n.t('errors.generation_error').toString())
		} finally {
			// Refresh either way: the PDF may exist even when the attachments patch failed.
			this.fireToSpa('invoiceUpdated', { invoiceId })
			// Resolving late is what lets the SPA start the download once the PDF actually exists.
			resolveSpaCall?.(result)
		}
	}

	onInvoiceEmailClose(): void {
		this.isInvoiceEmailDialogOpen = null
		this.invoiceEmailCallback?.(null)
		this.invoiceEmailCallback = null
	}

	onInvoiceEmailSent(event?: any): void {
		this.fireToSpa('invoiceUpdated', event)
		this.invoiceEmailCallback?.(event)
		this.invoiceEmailCallback = null
		this.isInvoiceEmailDialogOpen = null
	}

	onExternalDownloadClose(): void {
		this.isExternalDownloadDialogOpen = null
		this.externalDownloadCallback?.(null)
		this.externalDownloadCallback = null
	}

	beforeDestroy(): void {
		window.removeEventListener('message', this.msgEventListener)
	}

	async navigateTo(page: string) {
		await this.$nextTick()
		// A page with a query is a deep link (a push notification, a mail button) and must be shown
		// as is; only a bare page falls back to where the user last was under it.
		this.lastVisitedUrl = isV3DeepLink(page)
			? page
			: (window.localStorage.getItem(this.localStorageKey) ?? page)
		this.iframeElement?.contentWindow?.postMessage(`navigate:${this.lastVisitedUrl}`, '*')
	}

	async handleLoadingBillModal(
		args: { transportId: string; terminalVisitId: string },
		callback: (response?: any) => void
	): Promise<void> {
		this.transportAndVisitLoading = true
		let transportQuery = Promise.resolve(null as any)
		let terminalVisitQuery = Promise.resolve(null as any)
		if (args.transportId) {
			transportQuery = Transport.find(args.transportId)
		}
		if (args.terminalVisitId) {
			terminalVisitQuery = TerminalVisit.with([
				'product',
				'vessel',
				'terminal',
				'terminal.company',
				'terminal.ownedBy',
				'extractionArea',
				'vessel.company'
			]).find(args.terminalVisitId)
		}

		const transport = (await transportQuery)?.getData() ?? null
		const terminalVisit = (await terminalVisitQuery)?.getData() ?? null

		this.billOfLadingType =
			terminalVisit?.transshipmentDirection === 'unloading' ? 'unloading-bill' : 'loading-bill'
		this.transportAndVisitLoading = false

		this.openBillOfLadingGenerator = {
			transport,
			terminalVisit,
			onSubmit: () => {
				callback()

				this.openBillOfLadingGenerator = null
			}
		}
	}

	async handleVesselDocumentsModal(args: { vesselId: string }, callback: (response?: any) => void): Promise<void> {
		this.vesselId = args.vesselId
		this.isVesselDocumentsModalOpen = true
		callback()
	}

	async handleLoadingBillFormModal(callback: (response?: any) => void): Promise<void> {
		this.isLoadingBillFormModalOpen = true
		callback()
	}

	async handleProfileUpdated(args: { done: boolean } = { done: false }): Promise<void> {
		await User.sync(true)
		if (args.done) {
			await this.$router.push('/')
		}
	}

	fireToSpa(event: string, payload?: any): void {
		const spaUrl = process.env.VUE_APP_TERMINAL_MODULE_URL || window.location.origin
		const targetOrigin = spaUrl.startsWith('http') ? new URL(spaUrl).origin : window.location.origin

		this.iframeElement?.contentWindow?.postMessage(
			`xframe:fire:${event}:${JSON.stringify(payload ?? {})}`,
			targetOrigin
		)
	}
}
